import { useSelector } from "react-redux";

import {
  selectHideModalCloseBtn,
  selectModal,
  selectOnModalBack,
  selectOnModalClose,
} from "../../../redux/slices/appSlice";
import { closeModal } from "../../../assets/Utils/modal";
import { useEffect, type ReactNode } from "react";
import Icon from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import useClickOutside from "../../../_Web/hooks/useClickOutside";
import {
  motion,
  useDragControls,
  useMotionValue,
  AnimatePresence,
} from "framer-motion";
import { useMediaQuery } from "react-responsive";

export interface ModalPageProps {
  cancelButton?: boolean;
  small?: boolean;
  className?: string;
  style?: any;
  onBack?(): any;
  onClose?(): any;
  content: ReactNode;
  native?: boolean;
}

export function NewModalPage({
  content,
  small,
  className,
  style,
  native,
}: ModalPageProps) {
  const hideModalCloseBtn = useSelector(selectHideModalCloseBtn);

  const onClose = useSelector(selectOnModalClose);
  const onBack = useSelector(selectOnModalBack);

  const handleClose = () => {
    closeModal();
    onClose?.();
  };

  const ref = useClickOutside(() => {
    handleClose();
  });

  const dragControls = useDragControls();
  const dragY = useMotionValue(0);

  const isMobile = useMediaQuery({ query: "(max-width: 1024px)" });

  useEffect(() => {
    if (isMobile) {
      document.querySelector("html")!.classList.add("scroll-lock");
      document.querySelector("body")!.classList.add("scroll-lock");
    }

    if (!isMobile) {
      document.querySelector("html")!.classList.remove("scroll-lock");
      document.querySelector("body")!.classList.remove("scroll-lock");
    }

    return () => {
      if (isMobile) {
        document.querySelector("html")!.classList.remove("scroll-lock");
        document.querySelector("body")!.classList.remove("scroll-lock");
      }
    };
  }, [isMobile]);

  return (
    <motion.section
      ref={ref}
      style={{
        ...style,
        y: dragY,
      }}
      className={
        className !== undefined
          ? className
          : "self-end md:self-auto max-w-[900px] rounded-t-3xl md:rounded-3xl h-[91dvh] md:h-[80dvh] overflow-hidden bg-white"
      }
      initial={{
        y: 100,
        opacity: 0,
      }}
      animate={{
        y: 0,
        opacity: 1,
      }}
      exit={{
        y: 100,
        opacity: 0,
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
      layout
      drag={native && isMobile ? "y" : false}
      dragListener={false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{
        top: 0,
        bottom: 0.2,
      }}
      dragTransition={{ bounceStiffness: 500, bounceDamping: 15 }}
      dragDirectionLock
      whileDrag={{ cursor: "grabbing" }}
      dragControls={dragControls}
      onDragEnd={() => {
        if (dragY.get() > 20) {
          handleClose();
        }
      }}
    >
      {native && isMobile && (
        <motion.div
          className="flex justify-center h-12"
          layout
          onPointerDown={(event) => {
            dragControls.start(event);
          }}
        >
          <div className="h-1 w-10 bg-[#E8E8E8] rounded-full mt-2.5" />
        </motion.div>
      )}
      {!hideModalCloseBtn && (!native || !isMobile) && (
        <>
          <Icon
            onClick={handleClose}
            size={25}
            icon="close"
            className="close-modal"
          />
        </>
      )}
      {onBack ? (
        <>
          <Icon
            onClick={() => {
              onBack();
            }}
            size={25}
            icon="arrow_back"
            className="back-modal"
          />
        </>
      ) : null}
      {content}
    </motion.section>
  );
}

export default function NewModal() {
  const { modal, isNew } = useSelector(selectModal);

  return (
    <AnimatePresence>
      {modal && isNew && (
        <motion.div
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
          className="fixed z-[9999] h-dvh w-screen top-0 left-0 flex items-center justify-center bg-[rgba(0,0,0,0.5)]"
        >
          {modal}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
